/**
 * Monumenta Chart Theme Configuration
 *
 * Provides consistent color palette and styling for all chart components
 * to ensure brand compliance across the application.
 *
 * Usage:
 * import { monumentaChartColors, monumentaChartTheme } from '@/lib/components/charts/monumenta-chart-theme'
 */

/**
 * Monumenta Brand Color Palette for Charts
 */
export const monumentaChartColors = {
  // Primary Brand Colors
  primary: '#D2FA52',           // brand-primary - Signature Lime Green
  secondary: '#4760E0',         // brand-blue - Interactive Blue
  accent: '#D90A2C',            // brand-red - Call-to-Action Red

  // Semantic Colors
  success: '#10B981',           // Success Green
  warning: '#F59E0B',           // Warning Orange
  error: '#EF4444',             // Error Red
  info: '#3B82F6',              // Info Blue

  // Background Colors
  background: {
    primary: '#24262B',         // brand-dark-light
    secondary: '#2A2D34',       // brand-dark-lighter
    dark: '#232429',            // brand-dark
  },

  // Text Colors
  text: {
    primary: '#FFFFFF',         // White
    secondary: '#838998',       // brand-gray
    tertiary: '#9CA3AF',        // brand-gray-light
  },

  // Grid and Border Colors
  grid: 'rgba(131, 137, 152, 0.2)',      // brand-gray with opacity
  gridLight: 'rgba(131, 137, 152, 0.1)', // lighter grid
  border: 'rgba(131, 137, 152, 0.3)',    // border color

  // Multi-series Colors (for charts with multiple data series)
  series: [
    '#D2FA52',  // brand-primary
    '#4760E0',  // brand-blue
    '#F59E0B',  // warning
    '#10B981',  // success
    '#8B5CF6',  // purple
    '#EC4899',  // pink
    '#14B8A6',  // teal
    '#F97316',  // orange
  ],

  // Opacity variants for fills
  opacity: {
    high: 0.8,
    medium: 0.5,
    low: 0.2,
    veryLow: 0.1,
  }
}

/**
 * Recharts-specific theme configuration
 */
export const monumentaChartTheme = {
  // Axis styling
  axis: {
    style: {
      axis: {
        stroke: monumentaChartColors.grid
      },
      ticks: {
        stroke: monumentaChartColors.grid
      },
      tickLabels: {
        fill: monumentaChartColors.text.secondary,
        fontSize: 10,
        fontFamily: 'Inter, system-ui, sans-serif'
      }
    }
  },

  // Grid styling
  grid: {
    stroke: monumentaChartColors.grid,
    strokeDasharray: '3 3'
  },

  // Tooltip styling
  tooltip: {
    contentStyle: {
      backgroundColor: monumentaChartColors.background.primary,
      border: 'none',
      borderRadius: '3px',
      padding: '8px 12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    },
    labelStyle: {
      color: monumentaChartColors.text.secondary,
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '4px',
    },
    itemStyle: {
      color: monumentaChartColors.text.primary,
      fontSize: '11px',
      fontWeight: 600,
      fontFamily: 'Inter, system-ui, sans-serif'
    }
  },

  // Legend styling
  legend: {
    wrapperStyle: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '10px',
      color: monumentaChartColors.text.secondary,
    },
    iconType: 'circle' as const,
  }
}

/**
 * Helper function to get a series color by index
 * Wraps around if index exceeds available colors
 */
export const getSeriesColor = (index: number): string => {
  return monumentaChartColors.series[index % monumentaChartColors.series.length]
}

/**
 * Helper function to get color with custom opacity
 */
export const getColorWithOpacity = (color: string, opacity: number): string => {
  // If color is already rgba, return as is
  if (color.startsWith('rgba')) return color

  // If color is hex, convert to rgba
  if (color.startsWith('#')) {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }

  return color
}

/**
 * Preset configurations for common chart types
 */
export const monumentaChartPresets = {
  // Radar Chart (Performance Evaluations)
  radar: {
    primary: {
      stroke: monumentaChartColors.primary,
      fill: monumentaChartColors.primary,
      fillOpacity: monumentaChartColors.opacity.low,
      strokeWidth: 2,
    },
    secondary: {
      stroke: monumentaChartColors.secondary,
      fill: monumentaChartColors.secondary,
      fillOpacity: monumentaChartColors.opacity.veryLow,
      strokeWidth: 2,
    },
    tertiary: {
      stroke: monumentaChartColors.warning,
      fill: monumentaChartColors.warning,
      fillOpacity: monumentaChartColors.opacity.veryLow,
      strokeWidth: 2,
    }
  },

  // Bar Chart
  bar: {
    fill: monumentaChartColors.primary,
    radius: [8, 8, 0, 0] as [number, number, number, number],
  },

  // Line Chart
  line: {
    stroke: monumentaChartColors.primary,
    strokeWidth: 2,
    dot: { fill: monumentaChartColors.primary, r: 4 },
    activeDot: { r: 6 },
  },

  // Pie Chart Colors
  pie: [
    '#10B981', // Excelente - success
    '#3B82F6', // Muito Bom - info
    '#8B5CF6', // Bom - purple
    '#F59E0B', // Adequado - warning
    '#EF4444', // Precisa Melhorar - error
  ]
}

/**
 * Custom tooltip formatter for performance scores
 */
export const formatPerformanceTooltip = (value: any, name: string, props: any) => {
  if (typeof value === 'number') {
    return [`${value.toFixed(2)}`, name]
  }
  return [value, name]
}

/**
 * Custom label formatter for charts
 */
export const formatChartLabel = (value: any) => {
  if (typeof value === 'number') {
    return value.toFixed(1)
  }
  return value
}

export default {
  colors: monumentaChartColors,
  theme: monumentaChartTheme,
  presets: monumentaChartPresets,
  getSeriesColor,
  getColorWithOpacity,
  formatPerformanceTooltip,
  formatChartLabel,
}
